<template>
  <spinner v-if="loading" />
  <div v-else-if="stage === 1" class="container-positions">
    <div v-for="(totalGroup, aIx) in groupedTotals" :key="aIx">
      <div class="box-position">
        <!-- Title of box (mySide) -->
        <my-position-header
          :game="game"
          :obj-side="totalGroup"
          :type-bet="'total'"
          :key-obj="aIx"
          @change-accordeon-opened="changeAccordeonOpened"
        />
        <div v-if="totalGroup.expanded">
          <div v-if="totalGroup.over">
            <my-position-accordeon
              v-if="totalGroup.over.bets.length > 1"
              :obj-side="totalGroup.over"
              :type-bet="'total'"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="totalGroup.over"
              :bet="totalGroup.over.bets[0]"
              :type-bet="'total'"
              :league="league"
              @display-bet="displayBet"
            />
          </div>

          <div v-if="totalGroup.under">
            <my-position-accordeon
              v-if="totalGroup.under.bets.length > 1"
              :obj-side="totalGroup.under"
              :type-bet="'total'"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="totalGroup.under"
              :bet="totalGroup.under.bets[0]"
              :type-bet="'total'"
              :league="league"
              @display-bet="displayBet"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="stage === 2">
    <button class="button-back mb-1" @click="stage = 1">
      ⇠ Return To Summary
    </button>
    <double-bet-info-modal :bet="session" />
  </div>
</template>

<script setup>
//CORE
import {
  calculateWin,
  getSideString,
  signOdds,
  fixed2,
  calculateAverageOdds,
  getCommissionPct,
  commissionAdjustedRisk,
  commissionAdjWin,
} from '@/core/utils';
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/stores/user';
import { useMiscStore } from '@/stores/store';
import { emitter } from '../../../core/emitter';

//COMPONENTS
import MyPositionAccordeon from '@/components/userBets/MyPositionAccordeon.vue';
import MyPositionAccordeonItem from '@/components/userBets/MyPositionSingleBet.vue';
import MyPositionHeader from '@/components/userBets/myPosition/MyPositionHeader.vue';
import Spinner from '@/components/global/Spinner.vue';
import DoubleBetInfoModal from '@/components/userBets/modals/DoubleBetInfoModal.vue';

//PROPS
const props = defineProps({
  game: {
    type: Object,
    default: null,
  },
  totalOver: {
    type: Object,
    required: true,
  },
  totalUnder: {
    type: Object,
    required: true,
  },
  totalRisk: {
    type: Object,
    required: true,
  },
  avgOddsTotals: {
    type: Number,
    required: true,
  },
  groupedBets: {
    type: Object,
    required: true,
  },
  league: {
    type: String,
    default: '',
  },
});

//CONST
const { user } = useUserStore();
const store = useMiscStore();
const session = ref({});
const over = ref([]);
const under = ref([]);
const stage = ref(1);
const dataOver = ref(false);
const dataUnder = ref(false);
const loading = ref(true);
const totalNameOver = ref('');
const totalNameUnder = ref('');
const myTotalOver = ref('');
const myTotalUnder = ref('');
const overRiskWin = ref([]);
const underRiskWin = ref([]);
const underOpened = ref(false);
const overOpened = ref(false);
const groupedTotals = ref(null);

//COMPUTED
const underOnlyBets = computed(() => {
  const obj = under.value[0];
  delete obj.sumRisk;
  delete obj.sumWin;
  return obj;
});

const overOnlyBets = computed(() => {
  const obj = over.value[0];
  delete obj.sumRisk;
  delete obj.sumWin;
  return obj;
});

const computedCommissionRate = computed(() => {
  if (!user.viewOddsWithCommission) return null;
  return getCommissionPct(
    user.commissionCharged,
    props.league,
    'total',
    store.commissionDiscounts,
  );
});

//HOOK
onMounted(async () => {
  over.value = [Object.assign({}, props.totalOver)];
  over.value = Object.assign([], over.value);
  under.value = [Object.assign({}, props.totalUnder)];
  under.value = Object.assign([], under.value);
  myTotalOver.value = Object.keys(over.value[0]);
  myTotalUnder.value = Object.keys(under.value[0]);
  if (myTotalOver.value[0]) {
    totalNameOver.value = over.value[Object.keys(over.value)][
      myTotalOver.value[0]
    ][0].game.participants[0]
      ? over.value[Object.keys(over.value)][myTotalOver.value[0]][0].game
          .participants[0].longName
      : null;
    overRiskWin.value =
      over.value[Object.keys(over.value)][myTotalOver.value[0]].avgOddsSum;
    dataOver.value = true;
  }
  if (myTotalUnder.value[0]) {
    totalNameUnder.value = under.value[Object.keys(under.value)][
      myTotalUnder.value[0]
    ][0].game.participants[1]
      ? under.value[Object.keys(under.value)][myTotalUnder.value[0]][0].game
          .participants[1].longName
      : null;
    underRiskWin.value =
      under.value[Object.keys(under.value)][myTotalUnder.value[0]].avgOddsSum;
    dataUnder.value = true;
  }
  loading.value = false;

  groupByTotal();
});

//FUNCTIONS
function groupByTotal() {
  const grouped = {};
  const underTotalKeys = Object.keys(underOnlyBets.value);
  const overTotalKeys = Object.keys(overOnlyBets.value);
  let accumOverRisk = 0;
  let accumUnderRisk = 0;

  const totalsKeys = [...underTotalKeys, ...overTotalKeys];

  totalsKeys.forEach((s) => {
    if (!grouped[Math.abs(s)]) {
      grouped[Math.abs(s)] = {
        under: false,
        over: false,
        mySide: false,
        expanded: true,
      };
    }

    if (underOnlyBets.value[s]) {
      const { sumRiskBets, sumWinBets, participantInfo, gameObj } =
        reduceRiskWinByBet(underOnlyBets.value[s]);
      const objUnder = {
        bets: underOnlyBets.value[s],
        sumRiskBets,
        sumWinBets,
        realTotal: s,
        participantInfo,
        gameObj,
        avgOdds: calculateAvgOddsTotals(sumRiskBets, sumWinBets),
      };

      accumUnderRisk = sumRiskBets;
      grouped[Math.abs(s)].under = objUnder;
    }

    if (overOnlyBets.value[s]) {
      const { sumRiskBets, sumWinBets, participantInfo, gameObj } =
        reduceRiskWinByBet(overOnlyBets.value[s]);
      const objOver = {
        bets: overOnlyBets.value[s],
        sumRiskBets,
        sumWinBets,
        realTotal: s,
        participantInfo,
        gameObj,
        avgOdds: calculateAvgOddsTotals(sumRiskBets, sumWinBets),
      };

      accumOverRisk = sumRiskBets;
      grouped[Math.abs(s)].over = objOver;
    }
  });

  Object.keys(grouped).forEach((k) => {
    grouped[k].mySide = getBestSideBet(grouped[k].under, grouped[k].over);
  });
  groupedTotals.value = grouped;
}

function reduceRiskWinByBet(bets) {
  let sumRiskBets = 0;
  let sumWinBets = 0;
  let participantInfo;
  let gameObj;
  if (user.viewOddsWithCommission) {
    bets.forEach((bet) => {
      const betRisk =
        bet.origin === 'offer'
          ? bet.bet
          : commissionAdjustedRisk(
              bet.bet,
              bet.odds,
              computedCommissionRate.value,
            );
      const tempWin = calculateWin(bet.bet, bet.odds);
      const betWin =
        bet.origin === 'offer'
          ? tempWin
          : commissionAdjWin(tempWin, computedCommissionRate.value);
      sumRiskBets += betRisk;
      sumWinBets += betWin;
      participantInfo = bet.game.participants.find(
        (p) => p.id === bet.participantID,
      );
      gameObj = bet.game;
    });
  } else {
    bets.forEach((bet) => {
      sumRiskBets += bet.bet;
      sumWinBets += calculateWin(bet.bet, bet.odds);
      participantInfo = bet.OU;
      gameObj = bet.game;
    });
  }
  return {
    sumRiskBets,
    sumWinBets,
    participantInfo,
    gameObj,
  };
}

function getBestSideBet(underObj, overObj) {
  const accumUnderRisk =
    underObj.sumRiskBets !== undefined ? parseFloat(underObj.sumRiskBets) : 0;
  const accumUnderWin =
    underObj.sumWinBets !== undefined ? parseFloat(underObj.sumWinBets) : 0;
  const accumOverRisk =
    overObj.sumRiskBets !== undefined ? parseFloat(overObj.sumRiskBets) : 0;
  const accumOverWin =
    overObj.sumWinBets !== undefined ? parseFloat(overObj.sumWinBets) : 0;

  const objBigWin = accumUnderWin > accumOverWin ? underObj : overObj;

  const winOverMinusRiskUnder = accumOverWin - accumUnderRisk;
  const winUnderMinusRiskOver = accumUnderWin - accumOverRisk;

  const calculatedRisk =
    accumUnderRisk > accumOverRisk
      ? accumUnderRisk - accumOverWin
      : accumOverRisk - accumUnderWin;

  const calculatedWin =
    accumUnderRisk > accumOverRisk
      ? accumUnderWin - accumOverRisk
      : accumOverWin - accumUnderRisk;

  return {
    totalName: winUnderMinusRiskOver > winOverMinusRiskUnder ? 'Under' : 'Over',
    total: objBigWin.realTotal,
    odds: calculateAvgOddsTotals(calculatedRisk, calculatedWin),
    risk: fixed2(calculatedRisk),
    win: fixed2(calculatedWin),
  };
}

function calculateAvgOddsTotals(risk, win) {
  const avgOdds = calculateAverageOdds(risk, win);
  const avgOddsABS =
    Number.isNaN(avgOdds) || Math.abs(avgOdds) === Infinity ? 0 : avgOdds;
  return avgOddsABS > -100 && avgOddsABS < 100 ? 0 : fixed2(avgOddsABS);
}

function changeAccordeonOpened(key) {
  groupedTotals.value[key].expanded = !groupedTotals.value[key].expanded;
}

//EMITTERS
//local
const emit = defineEmits(['close-modal']);

function close() {
  emit('close-modal');
}
//global
function displayBet(total) {
  session.value = total;
  emitter.emit('displayBet', session.value);
  loading.value = false;
  stage.value = 2;
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/tokens.scss' as main;

.container-positions {
  padding-left: 20px;
  padding-right: 20px;
}

.box-position {
  border-radius: 8px;
  background: main.$white_01;
  margin: 0;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 16px;
}

.title-position {
  background: main.$blue-702;
  color: main.$white;
  font-size: small;
  font-weight: bold;
  padding: 8px;
  margin: 0;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
  cursor: pointer;
}

.spacing-vertical-1 {
  height: 12px;
}

.betWin {
  background: main.$green-402;
}

.betLoss {
  background: main.$red-101;
}

.betDraw {
  background: main.$yellow-090;
}

//.betHalfWin
//   background: lightgreen

.betNormal {
  background: main.$black;
}

.bet {
  color: main.$blue-1100;
  font-size: small;
  margin: 0;
  width: 100%;
  border-top: 1px solid main.$grey-1000;
  background: main.$white;

  &-main-info {
    text-transform: uppercase;
    font-weight: main.$font-weight-bold;
    color: main.$color-primary;
    font-size: main.$font-size-sm;
  }

  &-risk {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }

  &-type {
    font-size: main.$font-size-sm;
    color: main.$blue-1100;
    font-weight: main.$font-weight-bold;
  }

  &-plus {
    font-size: main.$font-size-sm;
    color: main.$white;
    cursor: pointer;
    font-weight: main.$font-weight-bold;
  }

  &-time {
    font-size: x-small;
    color: main.$blue-1100;
    font-weight: main.$font-weight-normal;
    text-transform: uppercase;
  }

  &-teams {
    font-size: x-small;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      display: none;
    }
  }

  &-small {
    font-size: x-small;
    text-transform: uppercase;
  }
}

.win,
.lose,
.cancelled,
.refunded {
  margin: 0;
  padding: 0.35rem 2rem;
  display: inline-block;
  font-family: inherit;
  border-radius: 4px;
  font-size: main.$font-size-xs;
  color: main.$white;
}

.win {
  background: main.$green-302;
}

.lose {
  background: main.$red-500;
}

.smallText {
  display: none;
}

.cancelled,
.refunded {
  background: main.$grey-1701;
}

.bar-separate {
  text-align: center;
  font-size: 12px;
  color: main.$blue-201;
}

.button-back {
  width: 100%;
  background: main.$grey-500;
  color: main.$blue-1100;
  font-weight: bold;
}

svg[data-icon='chevron-up'],
svg[data-icon='chevron-down'] {
  color: main.$white;
}

svg[data-icon='plus'] {
  padding: 2px;
  font-size: 12px;
  background: main.$blue-404;
  border-radius: 360px;
}

svg[data-icon='plus']:hover {
  opacity: 0.9;
}

.short-info {
  font-size: small;
  font-weight: bold;
  color: main.$blue-702;
  text-align: center;
}

.jump {
  min-width: 10%;
}

.iconAccordeon {
  color: main.$color-primary;
}
</style>
